import { getWorkoutSufferScore } from './suffer.js';

export const CTL_DECAY = Math.exp(-1 / 42); // 42-day rolling constant for fitness
export const ATL_DECAY = Math.exp(-1 / 7);  // 7-day rolling constant for fatigue

/**
 * Normalizes workout timestamp across different schemas.
 * @param {Object} w - Workout object
 * @returns {number} Epoch timestamp in milliseconds
 */
export function getWorkoutTimestamp(w) {
  if (!w) return 0;
  if (w.timestamp) {
    const t = typeof w.timestamp === 'number' ? w.timestamp : Date.parse(w.timestamp);
    if (!isNaN(t)) return t;
  }
  if (w.startTime) {
    const parsed = Date.parse(w.startTime);
    if (!isNaN(parsed)) return parsed;
  }
  if (w.date) {
    if (typeof w.date === 'string') {
      const trimmed = w.date.trim();
      const match = trimmed.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})/);
      if (match) {
        return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10)).getTime();
      }
    }
    const parsed = Date.parse(w.date);
    if (!isNaN(parsed)) return parsed;
  }
  if (w.id && typeof w.id === 'number' && w.id > 1000000000000) return w.id;
  return 0;
}

/**
 * Runs a pure Banister impulse-response simulation over a sequence of daily pulse loads.
 * @param {number[]} dailyLoads - Array of daily load values
 * @param {number} [initialBaseline=0] - Initial CTL and ATL value
 * @returns {{ctl: number[], atl: number[], tsb: number[]}}
 */
export function simulateBanisterModel(dailyLoads, initialBaseline = 0) {
  let ctl = initialBaseline;
  let atl = initialBaseline;

  const ctlSeries = [];
  const atlSeries = [];
  const tsbSeries = [];

  for (let i = 0; i < dailyLoads.length; i++) {
    const load = dailyLoads[i] || 0;
    ctl = ctl * CTL_DECAY + load * (1 - CTL_DECAY);
    atl = atl * ATL_DECAY + load * (1 - ATL_DECAY);

    ctlSeries.push(ctl);
    atlSeries.push(atl);
    tsbSeries.push(ctl - atl);
  }

  return { ctl: ctlSeries, atl: atlSeries, tsb: tsbSeries };
}

/**
 * Formats a Date to 'sv' (YYYY-MM-DD) local format.
 * @param {Date} date
 * @returns {string}
 */
export function toLocalDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Calculates historical Banister CTL, ATL, and TSB (Form) curves from a list of workouts.
 * @param {Object[]} [workouts=[]] - Array of workout objects
 * @param {Object} [options={}] - Options including today date and todayHealth
 * @returns {Object} Performance metrics and historical time series
 */
export function calculateBanisterTimeSeries(workouts = [], options = {}) {
  let today;
  if (options && options.today) {
    if (typeof options.today === 'string' && options.today.includes('-')) {
      const parts = options.today.split('T')[0].split('-');
      today = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
    } else {
      today = new Date(options.today);
    }
  } else {
    today = new Date();
  }
  today.setHours(0, 0, 0, 0);
  const todayKey = toLocalDateKey(today);

  const h = options?.todayHealth || (typeof state !== 'undefined' && state && state.todayHealth ? state.todayHealth : null);

  const buildBiometricsEntry = (health) => {
    const entry = {
      pulseLoad: 0,
      workouts: [],
      recoveryScore: null,
      restingHr: null,
      restingHR: null,
      hrv: null,
      sleepHours: null,
      sleepScore: null
    };
    if (!health) return entry;
    const resting = health.restingHr ?? health.restingHR;
    if (resting !== undefined && resting !== null) {
      entry.restingHr = Number(resting);
      entry.restingHR = Number(resting);
    }
    if (health.hrv !== undefined && health.hrv !== null) {
      entry.hrv = Number(health.hrv);
    }
    if (health.sleepHours !== undefined && health.sleepHours !== null) {
      entry.sleepHours = Number(health.sleepHours);
    }
    if (health.sleepScore !== undefined && health.sleepScore !== null) {
      entry.sleepScore = Number(health.sleepScore);
    }

    let sleepVal = null;
    if (health.sleepScore !== undefined && health.sleepScore !== null) {
      sleepVal = Number(health.sleepScore);
    } else if (health.sleepHours !== null && health.sleepHours !== undefined) {
      sleepVal = Math.min(100, (Number(health.sleepHours) / 8) * 100);
    }
    let hrvVal = null;
    if (health.hrv !== null && health.hrv !== undefined) {
      hrvVal = Math.min(100, Math.max(0, ((Number(health.hrv) - 30) / 50) * 100));
    }

    let combinedRec = null;
    if (sleepVal !== null && hrvVal !== null) {
      combinedRec = Math.round(0.5 * sleepVal + 0.5 * hrvVal);
    } else if (sleepVal !== null) {
      combinedRec = Math.round(sleepVal);
    } else if (hrvVal !== null) {
      combinedRec = Math.round(hrvVal);
    }

    if (combinedRec !== null) {
      entry.recoveryScore = combinedRec;
    }
    return entry;
  };

  if (!workouts || workouts.length === 0) {
    const dateMap = {};
    if (h) {
      dateMap[todayKey] = buildBiometricsEntry(h);
    }
    return {
      fitness: 0,
      fatigue: 0,
      form: 0,
      sparklineData: [0, 0, 0, 0, 0, 0, 0],
      weeklyLoad: [0, 0, 0, 0, 0, 0, 0],
      historicalTimeSeries: dateMap[todayKey] ? [{
        dateStr: todayKey,
        dateLabel: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ctl: 0,
        atl: 0,
        tsb: 0,
        load: 0,
        recovery: dateMap[todayKey].recoveryScore,
        restingHr: dateMap[todayKey].restingHr,
        hrv: dateMap[todayKey].hrv,
        sleepHours: dateMap[todayKey].sleepHours,
        sleepScore: dateMap[todayKey].sleepScore
      }] : [],
      totalSufferSum: 0,
      dateMap
    };
  }

  const sortedWorkouts = [...workouts]
    .filter(w => getWorkoutTimestamp(w) > 0)
    .sort((a, b) => getWorkoutTimestamp(a) - getWorkoutTimestamp(b));

  if (sortedWorkouts.length === 0) {
    const dateMap = {};
    if (h) {
      dateMap[todayKey] = buildBiometricsEntry(h);
    }
    return {
      fitness: 0,
      fatigue: 0,
      form: 0,
      sparklineData: [0, 0, 0, 0, 0, 0, 0],
      weeklyLoad: [0, 0, 0, 0, 0, 0, 0],
      historicalTimeSeries: dateMap[todayKey] ? [{
        dateStr: todayKey,
        dateLabel: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        ctl: 0,
        atl: 0,
        tsb: 0,
        load: 0,
        recovery: dateMap[todayKey].recoveryScore,
        restingHr: dateMap[todayKey].restingHr,
        hrv: dateMap[todayKey].hrv,
        sleepHours: dateMap[todayKey].sleepHours,
        sleepScore: dateMap[todayKey].sleepScore
      }] : [],
      totalSufferSum: 0,
      dateMap
    };
  }

  const oldestTs = getWorkoutTimestamp(sortedWorkouts[0]);
  const newestTs = getWorkoutTimestamp(sortedWorkouts[sortedWorkouts.length - 1]);
  const minTs = Math.min(oldestTs, today.getTime());
  const maxTs = Math.max(today.getTime(), newestTs);

  const startDay = new Date(minTs);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(maxTs);
  endDay.setHours(0, 0, 0, 0);

  const dateMap = {};
  let curr = new Date(startDay);
  while (curr <= endDay) {
    const key = toLocalDateKey(curr);
    dateMap[key] = {
      pulseLoad: 0,
      workouts: [],
      recoveryScore: null
    };
    curr.setDate(curr.getDate() + 1);
  }

  let totalSufferSum = 0;
  sortedWorkouts.forEach(w => {
    const ts = getWorkoutTimestamp(w);
    if (!ts) return;
    const key = toLocalDateKey(new Date(ts));
    if (dateMap[key]) {
      if (!w.excludeFromStats) {
        const suffer = getWorkoutSufferScore(w);
        dateMap[key].pulseLoad += suffer;
        totalSufferSum += suffer;
      }
      dateMap[key].workouts.push(w);

      if (w.subjective) {
        const sub = w.subjective;
        let sleepVal = null;
        if (sub.sleepScore) {
          sleepVal = sub.sleepScore;
        } else if (sub.sleepHours !== undefined && sub.sleepHours !== null) {
          sleepVal = Math.min(100, ((sub.sleepHours + (sub.sleepMinutes || 0) / 60) / 8) * 100);
        }

        let hrvVal = null;
        if (sub.hrv) {
          hrvVal = Math.min(100, Math.max(0, ((sub.hrv - 30) / 50) * 100));
        }

        let combinedRec = null;
        if (sleepVal !== null && hrvVal !== null) {
          combinedRec = Math.round(0.5 * sleepVal + 0.5 * hrvVal);
        } else if (sleepVal !== null) {
          combinedRec = Math.round(sleepVal);
        } else if (hrvVal !== null) {
          combinedRec = Math.round(hrvVal);
        }
        if (combinedRec !== null) {
          dateMap[key].recoveryScore = combinedRec;
        }
      }
    }
  });

  // Support options.todayHealth or global state.todayHealth
  if (h && dateMap[todayKey]) {
    const bio = buildBiometricsEntry(h);
    dateMap[todayKey].restingHr = bio.restingHr;
    dateMap[todayKey].restingHR = bio.restingHR;
    dateMap[todayKey].hrv = bio.hrv;
    dateMap[todayKey].sleepHours = bio.sleepHours;
    dateMap[todayKey].sleepScore = bio.sleepScore;
    if (bio.recoveryScore !== null) {
      dateMap[todayKey].recoveryScore = bio.recoveryScore;
    }
  }

  const keys = Object.keys(dateMap).sort();
  const totalDaysCount = Math.max(1, keys.length);
  const avgDailyLoad = totalSufferSum / totalDaysCount;

  let ctl = avgDailyLoad;
  let atl = avgDailyLoad;

  const ctlValues = {};
  const atlValues = {};
  const tsbValues = {};

  keys.forEach(k => {
    const dailyLoad = dateMap[k].pulseLoad;
    ctl = ctl * CTL_DECAY + dailyLoad * (1 - CTL_DECAY);
    atl = atl * ATL_DECAY + dailyLoad * (1 - ATL_DECAY);

    ctlValues[k] = ctl;
    atlValues[k] = atl;
    tsbValues[k] = ctl - atl;
  });

  const latestKey = keys[keys.length - 1] || todayKey;
  const fitness = Math.round(ctlValues[latestKey] || 0);
  const fatigue = Math.round(atlValues[latestKey] || 0);
  const form = parseFloat((tsbValues[latestKey] || 0).toFixed(1));

  const historicalTimeSeries = keys.map(k => {
    const dParts = k.split('-');
    const dateObj = dParts.length === 3 ? new Date(parseInt(dParts[0], 10), parseInt(dParts[1], 10) - 1, parseInt(dParts[2], 10)) : new Date(k);
    return {
      dateStr: k,
      dateLabel: dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      ctl: Math.round(ctlValues[k] || 0),
      atl: Math.round(atlValues[k] || 0),
      tsb: parseFloat((tsbValues[k] || 0).toFixed(1)),
      load: dateMap[k] ? Math.round(dateMap[k].pulseLoad) : 0,
      recovery: dateMap[k] ? dateMap[k].recoveryScore : null,
      restingHr: dateMap[k] ? (dateMap[k].restingHr ?? null) : null,
      hrv: dateMap[k] ? (dateMap[k].hrv ?? null) : null,
      sleepHours: dateMap[k] ? (dateMap[k].sleepHours ?? null) : null,
      sleepScore: dateMap[k] ? (dateMap[k].sleepScore ?? null) : null
    };
  });

  // Rolling 7-day sparkline
  const sparklineData = [];
  const last7Keys = keys.slice(-7);
  while (last7Keys.length < 7) {
    last7Keys.unshift(keys[0] || todayKey);
  }
  last7Keys.forEach(k => {
    sparklineData.push(parseFloat((tsbValues[k] || 0).toFixed(1)));
  });

  return {
    fitness,
    fatigue,
    form,
    sparklineData,
    historicalTimeSeries,
    totalSufferSum,
    dateMap
  };
}
